import axios from "axios";
import { traceCall } from "../../utils/debug";
import { baseURL } from "../apirest";

/**
 * Thin wrapper over a Moodle REST web service call.
 * Moodle returns HTTP 200 even on errors, with an `exception` field, so we have
 * to inspect the body and throw explicitly.
 *
 * `baseUrl` is a parameter, not hardcoded, so this respects the localhost
 * Moodle override in contexts/config.js (baseMoodleURL) - same fix already
 * applied to services/moodle/speed.js and services/moodle/activity.js.
 */
const call = async ({ token, wsfunction, baseUrl = baseURL, params = {} }) => {
  const wsBase = `${baseUrl.replace(/\/$/, "")}/webservice/rest/server.php`;
  const search = new URLSearchParams({
    wstoken: token,
    moodlewsrestformat: "json",
    wsfunction,
  });
  Object.entries(params).forEach(([k, v]) => search.append(k, v));

  // Debug trace never logs the token.
  return traceCall(`ws:${wsfunction}`, { wsfunction, params }, async () => {
    const { data } = await axios.get(`${wsBase}?${search.toString()}`);
    if (data && data.exception) {
      throw new Error(data.message || data.errorcode || "Moodle web service error");
    }
    return data;
  });
};

/**
 * local_tipddy_ranking_get_ranking
 * @returns {Promise<{ranking: Array<{userid:number, points:number, rank:number}>}>}
 */
export const getRanking = async ({ token, courseid = 0, limit = 0, baseUrl }) =>
  call({
    token,
    wsfunction: "local_tipddy_ranking_get_ranking",
    baseUrl,
    params: { courseid, limit },
  });

/**
 * local_tipddy_ranking_get_test_ranking
 * @returns {Promise<{ranking: Array<{userid:number, grade:number, quizzescount:number, rank:number}>}>}
 */
export const getTestRanking = async ({
  token,
  courseid = 0,
  minquizzes = 10,
  limit = 0,
  baseUrl,
}) =>
  call({
    token,
    wsfunction: "local_tipddy_ranking_get_test_ranking",
    baseUrl,
    params: { courseid, minquizzes, limit },
  });

/**
 * local_tipddy_ranking_get_student_stats
 * @returns {Promise<{gpa:number, percentile:number, acertadas:number,
 *   falladas:number, no_contestadas:number, flagged_total:number,
 *   flagged_acertadas:number, flagged_falladas:number,
 *   flagged_no_contestadas:number}>}
 */
export const getStudentStats = async ({ token, userid, courseid = 0, baseUrl }) =>
  call({
    token,
    wsfunction: "local_tipddy_ranking_get_student_stats",
    baseUrl,
    params: { userid, courseid },
  });

/**
 * block_tipddy_rank_get_speed_stats
 *
 * Requires Tipddy Rank v1.3.2 (or later), the function exposed to
 * moodle_mobile_app, and the block/tipddy_rank:viewstats capability. The
 * payload is passed through unchanged so callers can render an isolated
 * unavailable state when Moodle does not provide this optional dataset.
 * @returns {Promise<{my_tests_done:number, my_answered:number,
 *   my_avg_seconds:number, tier_avg_seconds:number}>}
 */
export const getSpeedStats = async ({ token, userid, courseid = 0, baseUrl }) =>
  call({
    token,
    wsfunction: "block_tipddy_rank_get_speed_stats",
    baseUrl,
    params: { userid, courseid },
  });

/**
 * core_user_get_users_by_field — get_ranking only returns userids, so we need a
 * second call to resolve display names / avatars for the leaderboard.
 * @returns {Promise<Array<{id:number, fullname:string, profileimageurl:string}>>}
 */
export const getUsersByField = async ({ token, ids = [], baseUrl }) => {
  if (!ids.length) return [];
  const params = { field: "id" };
  ids.forEach((id, i) => {
    params[`values[${i}]`] = id;
  });
  return call({ token, wsfunction: "core_user_get_users_by_field", baseUrl, params });
};
